import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import Link from '@/components/Link'
import React, { useEffect, useState } from 'react'
import { SignupBrandingType } from '../../components/AuthRegister/types'
import { AddPhotoInputField } from '../../components/AuthRegister/Verification/AddPhotoInputField'
import { VerificationMessage } from '../../components/AuthRegister/VerificationMessage'
import { InstitutionPicker } from '../../components/Forms/InstitutionPicker'
import { JobTitlePicker } from '../../components/Forms/JobTitlePicker'
import { RadioList } from '../../components/Forms/Radio'
import { SpecialtiesPicker } from '../../components/Forms/SpecialtyPicker'
import { requireUser } from '../../hoc/with-user'
import {
  selectProfessionalIDLabel,
  selectSignupRoleOptions,
  selectSeniorityOptionsByRole,
} from '../../modules/categories/selectors'
import { useSelector } from '../../react-redux'
import { AuthenticatedUser } from '../../types/userTypes'
import browserStorage from '../../lib/browserStorage'

const Page = ({
  currentUser,
  branding = 'medshr',
}: {
  currentUser: AuthenticatedUser
  branding: SignupBrandingType
}) => {
  if (typeof location !== 'undefined' && /hcp/.test(location.search)) {
    delete currentUser.kind
  }

  const [form, setForm] = useState({ ...currentUser })

  const router = useRouter()

  const next = router.query.next ?? router.query.referer

  useEffect(() => {
    if (browserStorage.usable()) {
      if (!currentUser.needs_setup && !browserStorage.get('jpr')) {
        browserStorage.set('jpr', true)
        document.location.href =
          '/auth/register/continue' + document.location.search
      }
    }
  }, [currentUser.id])

  const stream: 'academic' | 'hcp' | 'individual' =
    form.kind === 'academic'
      ? 'academic'
      : form.kind === 'other'
        ? 'individual'
        : 'hcp'

  const handleChangeEvent = e => {
    setForm(form => ({ ...form, [e.target.name]: e.target.value }))
  }

  const handleChange = ({ name, value }) => {
    if (name === 'kind' && value !== form.kind) {
      setForm(form => ({
        ...form,
        [name]: value,
        seniority: '',
        job_title: '',
        specialty: form.kind || !form.specialty ? [] : form.specialty,
      }))
    } else {
      setForm(form => ({ ...form, [name]: value }))
    }
  }

  const hasName = form.first_name?.length > 0 && form.last_name?.length > 0

  const needsVerification = stream === 'academic' || stream === 'hcp'

  const validateForKind = (kind: string) => {
    if (kind === 'academic') {
      return (
        form.seniority?.length > 0 &&
        form.institutions?.length > 0 &&
        form.institutions[0]?.id
      )
    } else if (kind === 'other') {
      return (
        form.job_title?.length > 0 &&
        form.institutions?.length > 0 &&
        form.institutions[0]?.id
      )
    } else if (kind === 'student') {
      return (
        form.seniority?.length > 0 &&
        form.institutions?.length > 0 &&
        form.institutions[0]?.id
      )
    } else {
      return (
        form.job_title?.length > 0 &&
        form.institutions?.length > 0 &&
        form.institutions[0]?.id &&
        form.specialty?.length > 0
      )
    }
  }

  const canContinue =
    hasName && form.kind?.length > 0 && validateForKind(form.kind)

  return (
    <div className="page__content signup fs-unmask">
      <div className="signup__content">
        <div className="signup__intro">
          <p
            style={{
              justifyContent: 'center',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {'Complete your '}
            <VerifiedLogo />
            {form.kind === 'academic'
              ? ' MedShr Academic profile'
              : form.kind === 'other'
                ? ' MedShr Personal profile'
                : ' MedShr Clinical profile'}
          </p>
        </div>

        <form
          action="/api/profile/partial"
          method="post"
          className="auth-register"
          encType="multipart/form-data"
        >
          <input
            type="hidden"
            name="redirect"
            value={
              '/api/profile/verification-redirect' +
              (typeof next === 'string'
                ? '?next=' + encodeURIComponent(next)
                : '')
            }
          />

          <AddPhotoInputField
            canShareExternally
            className="field"
            name="avatar"
            label="Add a photo"
            value={
              form.has_avatar
                ? form.avatar_url.replace('{image_size}', 'profile-large')
                : undefined
            }
          />

          <div className="field">
            <label htmlFor="first_name">First name *</label>
            <input
              type="text"
              name="first_name"
              id="first_name"
              className="auth-register__input fs-unmask"
              autoComplete="given-name"
              onChange={handleChangeEvent}
              value={form.first_name ?? ''}
            />
          </div>

          <div className="field">
            <label htmlFor="last_name">Last name *</label>
            <input
              type="text"
              name="last_name"
              id="last_name"
              className="auth-register__input fs-unmask"
              autoComplete="family-name"
              onChange={handleChangeEvent}
              value={form.last_name ?? ''}
            />
          </div>

          {hasName ? (
            <>
              {stream === 'academic' ? (
                <AcademicProfileSetup profile={form} onChange={handleChange} />
              ) : stream === 'individual' ? (
                <IndividualProfileSetup
                  profile={form}
                  onChange={handleChange}
                />
              ) : !form.kind ? (
                <>
                  <div className="field">
                    <label>I am a… *</label>
                    <RolePicker value={form.kind} onChange={handleChange} />
                  </div>
                </>
              ) : (
                <ClinicalProfileSetup profile={form} onChange={handleChange} />
              )}
            </>
          ) : null}

          <button
            className="btn btn--large btn--fill"
            disabled={!canContinue}
            type="submit"
          >
            Continue
          </button>
        </form>

        {hasName ? null : (
          <VerificationMessage>
            Complete as much as possible to speed up your free membership of
            MedShr.
          </VerificationMessage>
        )}
      </div>
    </div>
  )
}

const ClinicalProfileSetup = ({ profile, onChange }) => {
  const roles = useSelector(selectSignupRoleOptions)
  const professionalIDLabel = useSelector(state =>
    selectProfessionalIDLabel(state, profile.professional_id_type)
  )
  const role = roles.find(([label, id]) => id === profile.kind)

  return (
    <>
      <div className="field">
        <label>I am a… *</label>
        <strong>{role[0]}</strong>
        <Link
          href={'?as=hcp'}
          onClick={e => {
            e.preventDefault()
            onChange({ name: 'kind', value: '' })
          }}
          className="low"
          style={{ float: 'right' }}
        >
          Change
        </Link>
        <input type="hidden" name="kind" value={profile.kind ?? ''} />
      </div>
      {profile.kind === 'student' ? (
        <div className="field">
          <label htmlFor="seniority">More specifically, I am a… *</label>
          <SeniorityPicker
            name="seniority"
            kind={profile.kind}
            value={profile.seniority}
            onChange={onChange}
          />
        </div>
      ) : (
        <div className="field">
          <input
            type="hidden"
            name="seniority"
            value={profile.seniority ?? ''}
          />
          <input
            type="hidden"
            name="job_title"
            value={profile.job_title ?? ''}
          />
          <JobTitlePicker
            label="My role is… *"
            name="job_title"
            kind={profile.kind}
            value={profile.job_title}
            allowFreeText
            onChange={onChange}
          />
        </div>
      )}
      {profile.kind === 'student' ? null : (
        <div className="field">
          <SpecialtiesPicker
            label="My main specialty is… *"
            name="specialty"
            value={profile.specialty ?? []}
            kind={profile.kind}
            onChange={onChange}
          />
        </div>
      )}
      <div className="field">
        <InstitutionPicker
          label={`My main place of ${
            profile.kind === 'student' ? 'study' : 'work'
          } is… *`}
          name="institutions[0]"
          value={
            profile.institutions.length ? profile.institutions[0] : undefined
          }
          onChange={({ name, value }) => {
            const institutions = [...profile.institutions]
            institutions[0] = value
            onChange({ name: 'institutions', value: institutions })
          }}
        />
      </div>
      <div className="field">
        <label htmlFor="professional_id">{professionalIDLabel}</label>
        <input
          type="text"
          id="professional_id"
          className="fs-unmask"
          name="professional_id"
          value={profile.professional_id ?? ''}
          onChange={e =>
            onChange({ name: e.target.name, value: e.target.value })
          }
        />
      </div>
    </>
  )
}

const AcademicProfileSetup = ({ profile, onChange }) => {
  return (
    <>
      <div className="field">
        <label>I am a… *</label>
        <SeniorityPicker
          name="seniority"
          kind={profile.kind}
          value={profile.seniority}
          onChange={onChange}
        />
        <input type="hidden" name="kind" value={profile.kind ?? ''} />
      </div>
      <p>
        If you are a doctor or healthcare professional in clinical practice or a
        medical / dental student please <a href={'?hcp'}>sign up here</a> for
        full access to MedShr clinical resources.
      </p>
      <div className="field">
        <InstitutionPicker
          label={`My main place of work / study is… *`}
          name="institutions[0]"
          value={
            profile.institutions.length ? profile.institutions[0] : undefined
          }
          onChange={({ name, value }) => {
            const institutions = [...profile.institutions]
            institutions[0] = value
            onChange({ name: 'institutions', value: institutions })
          }}
        />
      </div>
    </>
  )
}

const IndividualProfileSetup = ({ profile, onChange }) => {
  return (
    <>
      <div className="field">
        <input type="hidden" name="kind" value={profile.kind ?? ''} />
        <input type="hidden" name="seniority" value={profile.seniority ?? ''} />
        <input type="hidden" name="job_title" value={profile.job_title ?? ''} />
        <JobTitlePicker
          label="My professional role is… *"
          name="job_title"
          kind={profile.kind}
          value={profile.job_title}
          allowFreeText
          onChange={onChange}
        />
      </div>
      <div className="field">
        <InstitutionPicker
          label="My main place of work is… *"
          name="institutions[0]"
          value={
            profile.institutions.length ? profile.institutions[0] : undefined
          }
          onChange={({ name, value }) => {
            if (value) {
              const institutions = [...profile.institutions]
              institutions[0] = value
              onChange({ name: 'institutions', value: institutions })
            } else {
              onChange({ name: 'institutions', value: [] })
            }
          }}
        />
      </div>
    </>
  )
}

const RolePicker = ({ value, onChange }) => {
  const roles = useSelector(selectSignupRoleOptions)
  return (
    <>
      <RadioList
        name="kind"
        className="fs-unmask"
        radioClassName="fs-unmask"
        value={value}
        options={roles
          .filter(([label, value]) => value !== 'academic' && value !== 'other')
          .map(([label, value]) => ({ label, value }))}
        onChange={onChange}
      />
    </>
  )
}

const SeniorityPicker = ({ name, kind, value, onChange }) => {
  const seniorities = useSelector(
    state => selectSeniorityOptionsByRole(state)[kind]
  )
  return (
    <RadioList
      name={name}
      className="fs-unmask"
      radioClassName="fs-unmask"
      value={value}
      options={seniorities.map(([label, value]) => ({ label, value }))}
      onChange={onChange}
    />
  )
}

export default requireUser(Page)

const VerifiedLogo = () => (
  <svg
    width="27"
    height="25"
    viewBox="0 0 27 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_75_77"
      style={{ maskType: 'alpha' }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="27"
      height="25"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.04038 23.4663L0.223729 13.4072C-0.0263381 12.9746 -0.0263381 12.2672 0.223729 11.8347L6.04038 1.77569C6.29045 1.34316 6.90425 0.989258 7.40439 0.989258H19.0378C19.538 0.989258 20.1518 1.34316 20.4018 1.77569L26.2183 11.8347C26.4684 12.2672 26.4684 12.9746 26.2183 13.4072L20.4018 23.4663C20.1518 23.8989 19.538 24.2526 19.0378 24.2526H7.40439C6.90425 24.2526 6.29045 23.8989 6.04038 23.4663Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_75_77)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.03911 23.4663L0.222462 13.4072C-0.0276046 12.9746 -0.0276046 12.2672 0.222462 11.8347L6.03911 1.77569C6.28918 1.34316 6.90298 0.989258 7.40311 0.989258H19.0366C19.5367 0.989258 20.1505 1.34316 20.4006 1.77569L26.2171 11.8347C26.4671 12.2672 26.4671 12.9746 26.2171 13.4072L20.4006 23.4663C20.1505 23.8989 19.5367 24.2526 19.0366 24.2526H7.40311C6.90298 24.2526 6.28918 23.8989 6.03911 23.4663Z"
        fill="#90A0B7"
        stroke="white"
        strokeWidth="3.46442"
      />
    </g>
    <path
      d="M19.5096 14.3812C19.5096 14.4325 19.4959 14.4838 19.4709 14.5284C17.6726 17.7297 16.3259 18.9351 13.3799 20.9524C13.2782 21.0221 13.142 21.0222 13.0403 20.9525C10.1343 18.9611 8.7156 17.6715 6.94916 14.5284C6.92406 14.4837 6.91049 14.4325 6.91049 14.3812V7.99778C6.91049 7.88071 6.97858 7.77434 7.08491 7.72535C9.08249 6.80501 11.0831 5.89203 13.0843 4.97975C13.1634 4.94372 13.2541 4.94372 13.3332 4.97975C15.3351 5.89221 17.3369 6.80484 19.3351 7.72535C19.4415 7.77434 19.5096 7.88071 19.5096 7.99778V14.3812ZM12.5563 13.5353C12.6778 13.6567 12.8762 13.6516 12.9912 13.5241L15.9333 10.263C16.0442 10.1401 16.2337 10.1303 16.3568 10.241L17.0356 10.8523C17.1588 10.9632 17.1687 11.153 17.0577 11.2762L13.0468 15.7224C12.9318 15.85 12.7334 15.8551 12.6119 15.7336L10.0074 13.1289C9.89028 13.0118 9.89029 12.8218 10.0074 12.7047L10.6544 12.0577C10.7716 11.9405 10.9616 11.9405 11.0787 12.0577L12.5563 13.5353ZM18.0268 14.1621C18.0525 14.117 18.0663 14.0654 18.0663 14.0136V8.91926C18.0663 8.80197 17.999 8.69595 17.8924 8.64708C16.7218 8.11052 14.3074 7.00594 13.3325 6.5601C13.2532 6.52384 13.1632 6.52438 13.0839 6.56068L8.52884 8.64681C8.42216 8.69567 8.35376 8.80224 8.35376 8.91957V14.0138C8.35376 14.0656 8.36751 14.1172 8.39321 14.1623C9.72459 16.4951 10.8883 17.6909 13.0388 19.2038C13.1413 19.2759 13.2788 19.2759 13.3812 19.2038C15.5321 17.6905 16.6948 16.4951 18.0268 14.1621Z"
      fill="white"
    />
  </svg>
)

export const getServerSideProps = async context => {
  return {
    props: {
      ...(await serverSideTranslations(context.locale, ['common'])),
    },
  }
}
